import type { ReactNode } from 'react'

import type { PosShiftReport, PosTally } from '../types'
import {
  absoluteTime,
  chipStyle,
  clockTime,
  count,
  money,
  percent,
  shiftReportStyles,
  varianceLabel,
} from './pos-format'
import { StatusChip } from './status-chip'

interface ShiftReportViewProps {
  report: PosShiftReport
  /** Dimmed while a live X re-reads, so a moving figure is visibly moving. */
  isRefreshing?: boolean
}

/**
 * An X or a Z, laid out as the document it is.
 *
 * ONE component for both, because they are the same arithmetic asked at two
 * moments — see `PosShiftReport`. Two components would drift, and a back office
 * whose X was laid out differently from its Z would invite exactly the mistake
 * this screen exists to prevent: reading a provisional figure as a signed-off
 * one. What differs between them is the chip at the top, the line under it, and
 * whether the cash box has a count in it.
 *
 * Read top down, the order is the order the question comes in: what did we take,
 * how was it paid, does the drawer agree, and what came off along the way. The
 * caveats — a variance worth asking about, a sale that may belong to another
 * till — sit against the figure they qualify rather than in a footer, because a
 * warning you have to scroll to find is a warning nobody read.
 */
export function ShiftReportView({ report, isRefreshing = false }: ShiftReportViewProps) {
  const { figures, cashBox, attribution, shift } = report

  return (
    <div
      className={`flex flex-col gap-5 transition-opacity ${isRefreshing ? 'opacity-60' : ''}`}
      aria-busy={isRefreshing || undefined}
    >
      <Header report={report} />

      {/* ------------------------------------------------------------------ */}
      {/* Takings                                                            */}
      {/* ------------------------------------------------------------------ */}

      <section className="border-border bg-surface flex flex-col gap-3 rounded-2xl border p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-muted text-xs">Collected</span>
            <span className="text-2xl font-semibold tabular-nums">{money(figures.collected)}</span>
          </div>

          <p className="text-muted text-xs">
            <span className="text-foreground font-medium tabular-nums">
              {count(figures.transactions)}
            </span>{' '}
            {figures.transactions === 1 ? 'ticket' : 'tickets'} · avg{' '}
            <span className="text-foreground font-medium tabular-nums">
              {money(figures.averageTicket)}
            </span>
          </p>
        </div>

        <dl className="border-border grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-3 text-sm sm:grid-cols-4">
          <Figure
            label="Gross"
            value={money(figures.grossSales)}
            hint="At menu price, including what was later taken off."
          />
          <Figure
            label="Net"
            value={money(figures.netSales)}
            hint="Gross less corrections and discounts."
          />
          <Figure label="Tax" value={money(figures.tax)} />
          {/* `serviceCharge` is the fallback for a Z frozen before the API split
              the two: it held the delivery fee under the wrong name, and that
              stored document is served as it was signed off. */}
          <Figure label="Delivery" value={money(figures.delivery ?? figures.serviceCharge)} />
        </dl>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Payments                                                           */}
      {/* ------------------------------------------------------------------ */}

      <Section
        title="How it was paid"
        note="Each row is net of what that method gave back, so the rows add up to Collected exactly."
      >
        {figures.payments.length === 0 ? (
          <p className="text-muted text-xs">Nothing was taken in this period.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {figures.payments.map((split) => (
              <li key={split.method} className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="font-medium">{split.label}</span>
                  <span className="text-muted flex items-baseline gap-2 tabular-nums">
                    <span className="text-[0.7rem]">
                      {count(split.count)} {split.count === 1 ? 'txn' : 'txns'}
                    </span>
                    <span className="text-foreground w-20 text-end font-medium">
                      {money(split.amount)}
                    </span>
                    <span className="w-9 text-end text-[0.7rem]">{percent(split.share)}</span>
                  </span>
                </div>

                {/* A bar, not a pie. Five methods read as an ordered list far
                    better than as five wedges, and the share is already the
                    number beside it — this only makes the ranking scannable. */}
                <div className="bg-border h-1 overflow-hidden rounded-full" role="presentation">
                  <div
                    className="bg-accent h-full rounded-full"
                    style={{ width: `${Math.max(0, Math.min(100, split.share * 100))}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ------------------------------------------------------------------ */}
      {/* The drawer                                                         */}
      {/* ------------------------------------------------------------------ */}

      <Section title="Cash drawer">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <Figure label="Opening float" value={money(cashBox.openingFloat)} />
          <Figure label="Cash taken" value={money(cashBox.cashSales)} />
          <Figure label="Cash handed back" value={money(cashBox.cashRefunds)} />
          <Figure
            label="Expected"
            value={money(cashBox.expected)}
            hint={
              cashBox.declaredExpected === null
                ? 'Float plus the cash on the sales the server has.'
                : 'What the till declared at close.'
            }
          />
          <Figure
            label="Counted"
            value={cashBox.counted === null ? '—' : money(cashBox.counted)}
            hint={cashBox.counted === null ? 'Nobody has counted this drawer yet.' : undefined}
          />
          <Figure
            label="Variance"
            value={varianceLabel(cashBox.variance)}
            tone={cashBox.varianceNeedsAttention ? 'warning' : undefined}
            hint={varianceHint(cashBox.variance)}
          />
        </dl>

        {/* The disagreement, spelled out only when there is one. The till is the
            only party that knows about sales it took offline and has not
            uploaded, so a gap here is usually a backlog rather than a loss —
            and calling a register short by exactly the size of its own outbox
            is the one reading this panel must never invite. */}
        {cashBox.declaredExpected !== null &&
          cashBox.declaredExpected !== cashBox.expectedFromSales && (
            <p className="text-muted border-border rounded-xl border border-dashed px-3 py-2 text-xs">
              The till declared{' '}
              <span className="text-foreground font-medium tabular-nums">
                {money(cashBox.declaredExpected)}
              </span>{' '}
              expected; the sales the server holds come to{' '}
              <span className="text-foreground font-medium tabular-nums">
                {money(cashBox.expectedFromSales)}
              </span>
              . The variance is measured against the till&rsquo;s figure — it is the only side that
              knows about sales it took offline and has not uploaded yet.
            </p>
          )}

        {cashBox.note && (
          <p className="text-muted text-xs">
            <span className="text-foreground font-medium">Note at close:</span> {cashBox.note}
          </p>
        )}
      </Section>

      {/* ------------------------------------------------------------------ */}
      {/* Corrections                                                        */}
      {/* ------------------------------------------------------------------ */}

      <Section
        title="Corrections and discounts"
        note="Counted at list price. A comp netted into a discount is a comp nobody can find again."
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <TallyFigure label="Voids" tally={figures.voids} hint="Removed after the kitchen had it." />
          <TallyFigure label="Comps" tally={figures.comps} hint="Given away — a manager decided." />
          <TallyFigure label="Waste" tally={figures.waste} hint="Made and binned." />
          <TallyFigure label="Manual discounts" tally={figures.manualDiscounts} />
          <TallyFigure label="Promotional discounts" tally={figures.promotionalDiscounts} />
          <TallyFigure label="Refunds" tally={figures.refunds} hint="Already off Collected." />
        </dl>
      </Section>

      {/* ------------------------------------------------------------------ */}
      {/* Attribution                                                        */}
      {/* ------------------------------------------------------------------ */}

      {attribution.byWindow > 0 && (
        <p
          className={`rounded-xl border px-3 py-2 text-xs ${
            attribution.mayOverlap
              ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300'
              : 'text-muted border-border border-dashed'
          }`}
        >
          <span className="font-medium">
            {count(attribution.byWindow)} of {count(attribution.stamped + attribution.byWindow)}{' '}
            sales were matched by this drawer&rsquo;s time window
          </span>{' '}
          rather than by naming the drawer itself.{' '}
          {attribution.mayOverlap
            ? 'Another drawer was trading at this outlet over the same minutes, so those sales may appear on its report too.'
            : 'No other drawer traded here over the same minutes, so nothing is double-counted.'}
        </p>
      )}

      <p className="text-muted text-[0.7rem]">
        {shift.number} · opened {absoluteTime(shift.openedAt)}
        {shift.openedByName && ` by ${shift.openedByName}`}
        {shift.closedAt && ` · closed ${absoluteTime(shift.closedAt)}`}
        {shift.closedAt && shift.closedByName && ` by ${shift.closedByName}`}
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Header                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * What this is, and what window it covers.
 *
 * The window is stated rather than implied by "today", because the whole failure
 * mode of a shift report is reading one period's figures as another's. An open
 * drawer says "→ now" on purpose: the number underneath is still moving.
 */
function Header({ report }: { report: PosShiftReport }) {
  const { shift } = report
  const isOpen = !shift.closedAt

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex flex-col gap-1.5">
        <StatusChip style={chipStyle(shiftReportStyles, report.type)} />
        <p className="text-muted text-xs">
          {clockTime(shift.openedAt)} → {isOpen ? 'now' : clockTime(shift.closedAt)} ·{' '}
          {shift.registerCode ?? 'Register'}
          {report.branchName && ` · ${report.branchName}`}
        </p>
      </div>

      <p className="text-muted max-w-56 text-end text-xs">
        {report.final ? 'Frozen at close' : 'Read'} {absoluteTime(report.generatedAt)}
        <br />
        <span className="text-[0.7rem]">
          {report.final
            ? 'Never recomputed — it says today what it said on the night.'
            : 'Re-derived on every read. Nothing was written.'}
        </span>
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Pieces                                                                     */
/* -------------------------------------------------------------------------- */

/** Short, and never a guess: a zero variance is "balanced", not "fine". */
function varianceHint(variance: number | null): string | undefined {
  if (variance === null) return undefined
  if (variance < 0) return 'Short — less in the drawer than expected.'
  if (variance > 0) return 'Over — more in the drawer than expected.'

  return 'Balanced.'
}

function Section({
  title,
  note,
  children,
}: {
  title: string
  note?: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {children}
      {note && <p className="text-muted text-[0.7rem]">{note}</p>}
    </section>
  )
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'warning'
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd
        className={`font-medium tabular-nums ${
          tone === 'warning' ? 'text-amber-700 dark:text-amber-400' : ''
        }`}
      >
        {value}
      </dd>
      {hint && <p className="text-muted text-[0.7rem]">{hint}</p>}
    </div>
  )
}

/**
 * A count and an amount as one figure.
 *
 * "3 · $42.00", and a zero row reads "0 · $0.00" rather than a dash: "no voids
 * all shift" is a finding, and a dash would look like the report failed to ask.
 */
function TallyFigure({ label, tally, hint }: { label: string; tally: PosTally; hint?: string }) {
  const isQuiet = tally.count === 0

  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="flex items-baseline gap-1.5 tabular-nums">
        <span className={`font-medium ${isQuiet ? 'text-muted' : ''}`}>{count(tally.count)}</span>
        <span className={`text-xs ${isQuiet ? 'text-muted' : ''}`}>· {money(tally.amount)}</span>
      </dd>
      {hint && <p className="text-muted text-[0.7rem]">{hint}</p>}
    </div>
  )
}
